import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Wand2 } from 'lucide-react';

interface OrderOption {
  orderId: number;
  code: string;
  description: string;
}

interface AutoFillDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (orderId: number, hours: number) => void;
  orders: OrderOption[];
}

const AutoFillDialog: React.FC<AutoFillDialogProps> = ({ open, onClose, onConfirm, orders }) => {
  const { t } = useTranslation();
  const [hours, setHours] = useState<string>('8');
  const [selectedOrderId, setSelectedOrderId] = useState<string>('');

  // Auto-select first order when dialog opens
  useEffect(() => {
    if (open && orders.length > 0 && !selectedOrderId) {
      setSelectedOrderId(String(orders[0].orderId));
    }
  }, [open, orders, selectedOrderId]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setHours('8');
      setSelectedOrderId('');
    }
  }, [open]);

  const handleConfirm = () => {
    const numHours = parseFloat(hours);
    const orderId = parseInt(selectedOrderId, 10);
    if (numHours >= 0 && numHours <= 8 && orderId) {
      onConfirm(orderId, numHours);
      onClose();
    }
  };

  const exceeds8Hours = parseFloat(hours) > 8;

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleConfirm();
    }
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Wand2 className="h-5 w-5 text-orange-500" />
            {t('timesheet.autoFill.title', 'Auto-Fill Month')}
          </DialogTitle>
          <DialogDescription>
            {t('timesheet.autoFill.description', 'Select an order and hours to fill for all weekdays. Weekends and holidays will be skipped.')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Order selection */}
          <div className="space-y-2">
            <Label>{t('timesheet.autoFill.selectOrder', 'Select Order')}</Label>
            <Select value={selectedOrderId} onValueChange={setSelectedOrderId}>
              <SelectTrigger>
                <SelectValue placeholder={t('timesheet.autoFill.selectOrder', 'Select Order')} />
              </SelectTrigger>
              <SelectContent>
                {orders.map((order) => (
                  <SelectItem key={order.orderId} value={String(order.orderId)}>
                    <span className="font-mono text-xs">{order.code}</span>
                    <span className="ml-2 text-muted-foreground">{order.description}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Hours input */}
          <div className="space-y-2">
            <Label htmlFor="hours">{t('timesheet.autoFill.hoursPerDay', 'Hours per day')} (max 8h)</Label>
            <Input
              id="hours"
              type="number"
              min={0}
              max={8}
              step={0.5}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              onKeyDown={handleKeyDown}
              className="text-lg font-medium"
            />
            {exceeds8Hours && (
              <p className="text-sm text-destructive">
                {t('timesheet.errors.exceeds8Hours', 'Cannot exceed 8 hours per day')}
              </p>
            )}
          </div>

          <div className="flex gap-2">
            {[4, 6, 8].map((preset) => (
              <Button
                key={preset}
                variant="outline"
                size="sm"
                onClick={() => setHours(String(preset))}
                className={hours === String(preset) ? 'border-orange-500 bg-orange-50 dark:bg-orange-950' : ''}
              >
                {preset}h
              </Button>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={exceeds8Hours || parseFloat(hours) < 0 || !selectedOrderId}
            className="bg-orange-500 hover:bg-orange-600"
          >
            <Wand2 className="mr-2 h-4 w-4" />
            {t('timesheet.autoFill.confirm', 'Fill Month')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoFillDialog;
